/**
 * Tenant Preset Stylesheet
 *
 * Generates and injects a <style> tag containing CSS variable overrides
 * scoped to `html[data-tenant="<tenantId>"]` for both light and dark modes.
 *
 * This enables true multi-tenant theming where switching tenants
 * automatically applies the correct token set without rebuilding or
 * overriding the root CSS variables globally.
 *
 * The injected stylesheet uses high-specificity selectors:
 *   html[data-tenant="abc"] { --color-primary: 220 90% 56%; }
 *   html[data-tenant="abc"].dark { --color-primary: 217 91% 60%; }
 */

import type { TenantThemeConfiguration } from '@/types/theme_configuration';
import { create_theme_css_variables } from '@/uptk/core/theme/theme_css_variables';
import { resolve_theme_shape_defaults } from '@/uptk/core/theme/theme_stitch_import';
import type { ResolvedThemeModeConfiguration } from '@/types/theme_configuration';

const STYLE_TAG_ID = 'geode-tenant-preset-stylesheet';

/**
 * Resolve a theme mode configuration from the tenant theme configuration.
 */
const resolve_mode_config = (
  config: TenantThemeConfiguration,
  scope: string,
  mode: 'light' | 'dark',
): ResolvedThemeModeConfiguration | null => {
  const scopeConfig = config.scopes?.[scope];
  if (!scopeConfig) return null;
  const modeConfig = scopeConfig.modes?.[mode];
  if (!modeConfig) return null;
  return {
    tokens: modeConfig.tokens ?? {},
    assets: modeConfig.assets ?? {},
    typography: modeConfig.typography ?? { displayFontFamily: '', bodyFontFamily: '', labelFontFamily: '' },
    // UPT-1955 : un thème sans clé `shape` doit retomber sur le rayon par
    // défaut de la plateforme (8px), pas sur 0 — sinon la feuille de préréglage
    // écrasait le portail en « carré » alors que le thème ne le demande pas.
    shape: resolve_theme_shape_defaults(modeConfig.shape),
  };
};

/**
 * Generate CSS variable declarations for a specific tenant + scope + mode.
 */
const generate_mode_vars = (
  config: TenantThemeConfiguration,
  scope: string,
  mode: 'light' | 'dark',
): Record<string, string> => {
  const resolved = resolve_mode_config(config, scope, mode);
  if (!resolved) return {};
  return create_theme_css_variables(resolved);
};

/**
 * Build the full CSS text for tenant preset overrides.
 *
 * Generates rules for both light and dark modes under each scope,
 * so theme switching works without re-fetching.
 */
const build_tenant_preset_css = (
  tenantId: string,
  config: TenantThemeConfiguration,
): string => {
  const rules: string[] = [];
  const scopes = ['portal', 'public'];

  for (const scope of scopes) {
    for (const mode of ['light', 'dark'] as const) {
      const vars = generate_mode_vars(config, scope, mode);
      if (Object.keys(vars).length === 0) continue;

      const declarations = Object.entries(vars)
        .map(([key, value]) => `  ${key}: ${value};`)
        .join('\n');

      if (mode === 'light') {
        rules.push(`html[data-tenant="${tenantId}"] {\n${declarations}\n}`);
      } else {
        rules.push(`html[data-tenant="${tenantId}"].dark {\n${declarations}\n}`);
      }
    }
  }

  return rules.join('\n\n');
};

/**
 * Inject or update the tenant preset stylesheet in the document head.
 *
 * Call this after the tenant theme configuration is loaded.
 * If the same tenant ID is already injected, it will be updated in place.
 */
export const inject_tenant_preset_stylesheet = (
  tenantId: string | undefined,
  config: TenantThemeConfiguration | null,
): void => {
  if (typeof document === 'undefined') return;

  let styleTag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;

  if (!tenantId || !config) {
    // Remove the stylesheet if no tenant or config
    if (styleTag) {
      styleTag.remove();
    }
    return;
  }

  const cssText = build_tenant_preset_css(tenantId, config);

  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = STYLE_TAG_ID;
    styleTag.setAttribute('data-tenant-preset', tenantId);
    document.head.appendChild(styleTag);
  }

  styleTag.textContent = cssText;
  styleTag.setAttribute('data-tenant-preset', tenantId);
};

/**
 * Remove the tenant preset stylesheet.
 * Useful when signing out or switching tenants.
 */
export const remove_tenant_preset_stylesheet = (): void => {
  if (typeof document === 'undefined') return;
  const styleTag = document.getElementById(STYLE_TAG_ID);
  if (styleTag) {
    styleTag.remove();
  }
};

/**
 * Read back the current tenant preset CSS for diagnostics.
 */
export const get_tenant_preset_css = (
  tenantId: string,
  config: TenantThemeConfiguration,
): string => {
  return build_tenant_preset_css(tenantId, config);
};
